/**
 * 2.0.0: WelcomeScreen 欢迎页组件
 *
 * 当聊天无历史消息时显示，作为 ChatInterface 的空状态替换。
 * 包含品牌展示、特色功能介绍、可点击预设问题。
 *
 * 设计参考:
 * - 世界地图背景（通过 CSS class `.bg-world-map`）
 * - "Ai" 文字 Logo（A 根据主题切换颜色，i 始终橙色）
 * - 3 个特色图标（Utensils/MapPin/Smile）
 * - 4 个可点击预设问题（各配橙色 SVG 小图标）→ 点击后直接发送到聊天
 * - 亮/暗双主题完整适配
 */
import { useTranslation } from 'react-i18next';
import { motion, useReducedMotion } from 'framer-motion';
import { Utensils, MapPin, Smile } from 'lucide-react';

/**
 * 橙色配色的自定义 SVG 图标容器
 * 统一尺寸 16x16，圆角矩形橙色背景 + 白色填充图标
 * 替代原有的 💡 emoji，符合专业 UI 规范（不使用 emoji 作为 UI 图标）
 *
 * P0 修复：提取到组件外部避免每次渲染时重新创建，防止子树 unmount/mount
 */
const PresetIcon = ({ children }: { children: React.ReactNode }) => (
  <span className="inline-flex items-center justify-center w-5 h-5 rounded-md bg-orange-500 mr-2 shrink-0">
    {children}
  </span>
);

/** 餐叉图标 — 对应美食/餐厅类问题 */
const IconRestaurant = () => (
  <PresetIcon>
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2" /><path d="M7 2v20" /><path d="M21 15V2a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3zm0 0v7" />
    </svg>
  </PresetIcon>
);

/** 雪山图标 — 对应滑雪/户外活动类问题 */
const IconMountain = () => (
  <PresetIcon>
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="m8 3 4 8 5-5 5 15H2L8 3z" /><path d="m4.14 15.08 2.36-2.36a1 1 0 0 1 1.41 0l1.18 1.18" />
    </svg>
  </PresetIcon>
);

/** 地标建筑图标 — 对应景点/观光类问题 */
const IconLandmark = () => (
  <PresetIcon>
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="3" x2="21" y1="22" y2="22" /><line x1="6" x2="6" y1="18" y2="11" /><line x1="10" x2="10" y1="18" y2="11" /><line x1="14" x2="14" y1="18" y2="11" /><line x1="18" x2="18" y1="18" y2="11" /><polygon points="12 2 20 7 4 7" />
    </svg>
  </PresetIcon>
);

/** 咖啡杯图标 — 对应咖啡馆/休闲类问题 */
const IconCoffee = () => (
  <PresetIcon>
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 2v2" /><path d="M14 2v2" /><path d="M16 8a1 1 0 0 1 1 1v8a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4V9a1 1 0 0 1 1-1h14a2 2 0 0 1 2 2v1a2 2 0 0 1-2 2h-1" />
    </svg>
  </PresetIcon>
);

/**
 * 3 个特色功能定义（模块级常量，避免每次渲染重建）
 * 使用 Lucide React 图标，统一尺寸 32px（内容装饰级别）
 * 亮色: 橙色图标 + 橙色浅背景圆形
 * 暗色: 橙色图标 + 暗色背景 + 橙色边框
 */
const FEATURES = [
  {
    icon: Utensils,
    labelKey: 'welcomeFeatureFood',
    labelDefault: '当地美食',
  },
  {
    icon: MapPin,
    labelKey: 'welcomeFeatureNearby',
    labelDefault: '附近搜索',
  },
  {
    icon: Smile,
    labelKey: 'welcomeFeatureMood',
    labelDefault: '描述心情',
  },
];

/**
 * 4 个可点击预设问题定义（模块级常量）
 * 点击后直接作为聊天消息发送，实现"一键提问"体验
 * 每个问题配有语义化的橙色圆角小图标（替代原来的 💡 emoji）
 */
const PRESET_QUESTIONS = [
  { key: 'chatExample1', fallback: '新宿有哪些必吃的餐厅', icon: <IconRestaurant /> },
  { key: 'chatExample2', fallback: '推荐几个北海道滑雪胜地', icon: <IconMountain /> },
  { key: 'chatExample3', fallback: '东京哈利波特主题的景点', icon: <IconLandmark /> },
  { key: 'chatExample4', fallback: '附近的咖啡馆推荐', icon: <IconCoffee /> },
];


interface WelcomeScreenProps {
  /** 点击预设问题时的回调，传入问题文本 */
  onPresetClick: (question: string) => void;
}

export function WelcomeScreen({ onPresetClick }: WelcomeScreenProps) {
  const { t } = useTranslation();

  /**
   * P0 修复：尊重用户 prefers-reduced-motion 系统偏好
   * 当 shouldReduceMotion 为 true 时，Framer Motion 动画不播放
   */
  const shouldReduceMotion = useReducedMotion();

  return (
    <motion.div
      initial={shouldReduceMotion ? false : { opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: shouldReduceMotion ? 0 : 0.5 }}
      className="flex flex-col items-center justify-center h-full text-center px-4 relative"
    >
      {/* 2.0.5: 干净地图背景 + 飞机动画覆层
          思路：PNG 无飞机无虚线，SVG 覆层在内部叠加飞机动画
          viewBox 2912x1440 对应 PNG 像素尺寸
          preserveAspectRatio="xMidYMid slice" 与 object-fit:cover 对齐 */}
      <div className="travel-bg" aria-hidden="true">
        <img
          src="/assets/travel-map-bg.png"
          alt=""
          className="travel-bg__img"
          draggable={false}
        />
        {/* SVG 覆层：飞机沿航线飞行动画 */}
        {/* 2.0.8: meet 与地图 object-fit:contain 一致，飞机与地图同比例不拉伸 */}
        <svg
          className="travel-bg__planes"
          viewBox="0 0 2912 1440"
          preserveAspectRatio="xMidYMid meet"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            {/* P1 修复：飞机形状定义一次，通过 <use> 引用，避免重复 8 次
                俯视图（机头朝右 +X）：细长机身 + 后掠翼 + 尾翼
                宽约 110 viewBox 单位 */}
            <path id="planeShape"
              d="M55 0 L20 -5 L0 -40 L-15 -30 L-10 -6 L-40 -6 L-48 -18 L-55 -12 L-50 -6 L-55 0 L-50 6 L-55 12 L-48 18 L-40 6 L-10 6 L-15 30 L0 40 L20 5 Z"
              className="travel-plane-icon" />

            {/* 2.0.9: clipPath 裁剪区域 — 内缩 60px（约半个飞机宽度），
                确保飞机飞到边缘时被裁剪，不会溢出到留白区域 */}
            <clipPath id="mapClip">
              <rect x="60" y="60" width="2792" height="1320" />
            </clipPath>

            {/* 航线路径：坐标系 2912x1440，对应干净 PNG 地理位置
                Route1: 太平洋(左侧) → 北美中部
                Route2: 北美 → 大西洋 → 欧洲(巴黎)
                Route3: 欧洲 → 中亚 → 东亚(日本)
                Route4: 东亚 → 东南亚 → 澳大利亚
                Route5: 澳大利亚 → 印度洋 → 非洲 → 南美 → 太平洋(环球)
                2.0.9: route1/route5 起点从 x=80 调整到 x=200，远离左边界 */}
            <path id="route1" d="M 200,650 Q 380,280 700,320" />
            <path id="route2" d="M 720,310 Q 1050,60 1350,340" />
            <path id="route3" d="M 1380,330 Q 1850,100 2500,430" />
            <path id="route4" d="M 2520,450 Q 2620,750 2420,1050" />
            <path id="route5" d="M 2420,1060 Q 1700,1300 800,1000 Q 450,850 200,650" />

            {/* 逆时针航线（从东向西，走不同纬度与顺时针航线交叉）
                RouteR1: 东亚 → 中东/非洲（中纬度，与 route3/route5 交叉）
                RouteR2: 欧洲 → 南大西洋 → 南美（中低纬度，与 route2/route5 交叉）
                RouteR3: 南美 → 南太平洋 → 太平洋左侧（与 route1 交叉）
                2.0.9: routeR3 终点从 (100,380) 调整到 (250,500)，远离左上角 */}
            <path id="routeR1" d="M 2400,550 Q 1800,700 1200,650" />
            <path id="routeR2" d="M 1300,450 Q 900,700 500,850" />
            <path id="routeR3" d="M 600,800 Q 380,600 250,500" />
          </defs>

          {/* 2.0.9: 所有飞机放入 clipPath 裁剪组，飞到边缘时被裁剪，不会溢出到留白 */}
          <g clipPath="url(#mapClip)">

            {/* ===== 顺时针航班（从西向东飞行） ===== */}

            {/* 飞机1: 太平洋 → 北美，14秒一周 */}
            <g>
              <animateMotion dur="14s" repeatCount="indefinite" rotate="auto">
                <mpath href="#route1" />
              </animateMotion>
              <use href="#planeShape" />
            </g>

            {/* 飞机2: 北美 → 欧洲，12秒一周，延迟3秒 */}
            <g>
              <animateMotion dur="12s" repeatCount="indefinite" rotate="auto" begin="3s">
                <mpath href="#route2" />
              </animateMotion>
              <use href="#planeShape" />
            </g>

            {/* 飞机3: 欧洲 → 东亚，18秒一周，延迟1秒 */}
            <g>
              <animateMotion dur="18s" repeatCount="indefinite" rotate="auto" begin="1s">
                <mpath href="#route3" />
              </animateMotion>
              <use href="#planeShape" />
            </g>

            {/* 飞机4: 东亚 → 澳大利亚，10秒一周，延迟5秒 */}
            <g>
              <animateMotion dur="10s" repeatCount="indefinite" rotate="auto" begin="5s">
                <mpath href="#route4" />
              </animateMotion>
              <use href="#planeShape" />
            </g>

            {/* 飞机5: 环球返程弧线，22秒一周，延迟7秒 */}
            <g>
              <animateMotion dur="22s" repeatCount="indefinite" rotate="auto" begin="7s">
                <mpath href="#route5" />
              </animateMotion>
              <use href="#planeShape" />
            </g>

            {/* ===== 逆时针航班（从东向西飞行） ===== */}

            {/* 飞机6: 东亚 → 中东（逆时针），16秒一周，延迟2秒 */}
            <g>
              <animateMotion dur="16s" repeatCount="indefinite" rotate="auto" begin="2s">
                <mpath href="#routeR1" />
              </animateMotion>
              <use href="#planeShape" />
            </g>

            {/* 飞机7: 欧洲 → 南美（逆时针），13秒一周，延迟6秒 */}
            <g>
              <animateMotion dur="13s" repeatCount="indefinite" rotate="auto" begin="6s">
                <mpath href="#routeR2" />
              </animateMotion>
              <use href="#planeShape" />
            </g>

            {/* 飞机8: 南美 → 太平洋（逆时针），20秒一周，延迟4秒 */}
            <g>
              <animateMotion dur="20s" repeatCount="indefinite" rotate="auto" begin="4s">
                <mpath href="#routeR3" />
              </animateMotion>
              <use href="#planeShape" />
            </g>

          </g>
        </svg>
      </div>

      {/* 内容层（z-index 高于背景） */}
      <div className="relative z-10 w-full max-w-lg mx-auto">

        {/* "Ai" 文字 Logo
            A: 亮色黑色 / 暗色白色
            i: 始终橙色，带橙色圆点 */}
        <motion.div
          initial={shouldReduceMotion ? false : { scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: shouldReduceMotion ? 0 : 0.1, duration: shouldReduceMotion ? 0 : 0.4 }}
          className="mb-6"
        >
          {/* P2 修复：添加 role="heading" + aria-label 语义标记 */}
          <span className="text-5xl font-bold font-serif tracking-tight" role="heading" aria-level={1} aria-label="AI Food Explorer">
            <span className="text-gray-900 dark:text-white" aria-hidden="true">A</span>
            <span className="text-orange-500" aria-hidden="true">i</span>
          </span>
        </motion.div>

        {/* 欢迎卡片 — 居中白色/暗色半透明卡片 */}
        <motion.div
          initial={shouldReduceMotion ? false : { y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: shouldReduceMotion ? 0 : 0.2, duration: shouldReduceMotion ? 0 : 0.5 }}
          className="
            bg-white/90 dark:bg-gray-900/80
            backdrop-blur-sm
            rounded-2xl
            shadow-2xl dark:shadow-none
            dark:border dark:border-orange-500/50
            p-6 sm:p-8
            mb-6
          "
        >
          {/* 标题：使用 sans 与正文一致，避免 font-serif(Noto Serif JP) 对部分中文缺字/度量不一致导致首字「欢」显小 */}
          <h2 className="text-xl sm:text-2xl font-bold font-sans text-gray-900 dark:text-white mb-2">
            {t('welcomeTitle', '欢迎来到日本探索AI助手')}
          </h2>

          {/* 副标题 / 标语 */}
          <p className="text-sm sm:text-base text-gray-500 dark:text-gray-400 mb-6">
            {t('chatWelcomeMessage')}
          </p>

          {/* 特色功能图标行
              亮色: 橙色浅背景圆形 + 橙色图标
              暗色: 暗色背景 + 橙色边框 + 橙色图标 */}
          <div className="flex items-center justify-center gap-6 sm:gap-8 mb-6">
            {FEATURES.map((feat, idx) => (
              <motion.div
                key={feat.labelKey}
                initial={shouldReduceMotion ? false : { opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: shouldReduceMotion ? 0 : 0.3 + idx * 0.1, duration: shouldReduceMotion ? 0 : 0.3 }}
                className="flex flex-col items-center gap-2 group"
              >
                {/* 图标容器 */}
                <div className="
                  w-14 h-14 sm:w-16 sm:h-16
                  rounded-full
                  flex items-center justify-center
                  bg-orange-50 dark:bg-gray-800
                  border-2 border-orange-200 dark:border-orange-500/60
                  group-hover:bg-orange-100 dark:group-hover:bg-gray-700
                  transition-colors duration-200
                ">
                  {/* P2 修复：装饰性图标添加 aria-hidden，避免屏幕阅读器重复朗读 */}
                  <feat.icon className="w-6 h-6 sm:w-7 sm:h-7 text-orange-500" aria-hidden="true" />
                </div>
                {/* 标签 */}
                <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 font-medium">
                  {t(feat.labelKey, feat.labelDefault)}
                </span>
              </motion.div>
            ))}
          </div>

          {/* 预设问题区域 — 可点击的快捷问题 */}
          <div className="space-y-2">
            {PRESET_QUESTIONS.map((q, idx) => (
              <motion.button
                key={q.key}
                initial={shouldReduceMotion ? false : { opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: shouldReduceMotion ? 0 : 0.5 + idx * 0.08, duration: shouldReduceMotion ? 0 : 0.3 }}
                onClick={() => onPresetClick(t(q.key, q.fallback))}
                aria-label={t(q.key, q.fallback)}
                className="
                  w-full text-left
                  flex items-center
                  px-4 py-2.5
                  rounded-xl
                  text-sm
                  bg-gray-50 dark:bg-gray-800/60
                  hover:bg-orange-50 dark:hover:bg-gray-700
                  text-gray-700 dark:text-gray-300
                  hover:text-orange-600 dark:hover:text-orange-400
                  border border-gray-100 dark:border-gray-700
                  hover:border-orange-200 dark:hover:border-orange-500/40
                  transition-all duration-200
                  cursor-pointer
                "
              >
                {q.icon} {t(q.key, q.fallback)}
              </motion.button>
            ))}
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}
